import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { MenuItem } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import * as FormDialog from "../shared/FormDialog";
import { ActionMenu, ActionMenuDeleteMenuItem } from "../shared/ActionMenu";
import { hasErrorCode } from "../api/errors";
import { PencilIcon } from "../shared/icons";
import {
  deleteEnterpriseCustomerOrganisationMutationOptions,
  ENTERPRISE_CUSTOMER_ORGANISATION_NAME_ALREADY_EXISTS,
  updateEnterpriseCustomerOrganisationMutationOptions,
  type EnterpriseCustomerOrganisation,
} from "../api/enterpriseCustomer";

type DialogState = "edit" | null;

export const OrganisationActionMenu = ({
  organisation,
  onDeleted,
  ariaLabelKey,
}: {
  organisation: EnterpriseCustomerOrganisation;
  onDeleted: () => void;
  ariaLabelKey?: TranslationKey;
}) => {
  const { t } = useTranslation();
  const deleteMutation = useMutation(deleteEnterpriseCustomerOrganisationMutationOptions());
  const [isOpen, setIsOpen] = useState(false);
  const [dialogState, setDialogState] = useState<DialogState>(null);

  const closeDialog = () => setDialogState(null);
  const openDialog = (state: Exclude<DialogState, null>) => {
    setDialogState(state);
    setIsOpen(false);
  };

  return (
    <>
      {dialogState === "edit" && <OrganisationEditDialog organisation={organisation} onClose={closeDialog} />}

      <ActionMenu isOpen={isOpen} onOpenChange={setIsOpen} ariaLabelKey={ariaLabelKey}>
        <MenuItem onClick={() => openDialog("edit")}>
          <PencilIcon />
          {t("EnterpriseCustomer.Organisation.Edit")}
        </MenuItem>

        <ActionMenuDeleteMenuItem
          variant="severe"
          onClose={() => setIsOpen(false)}
          onDelete={() => deleteMutation.mutateAsync(organisation.id)}
          onDeleteSuccess={onDeleted}
          confirmDeleteDialog={{
            header: t("EnterpriseCustomer.Organisation.DeleteDialog.Header"),
            message: t("EnterpriseCustomer.Organisation.DeleteDialog.Message", {
              name: organisation.organizationName,
            }),
          }}
        />
      </ActionMenu>
    </>
  );
};

const OrganisationEditDialog = ({
  organisation,
  onClose,
}: {
  organisation: EnterpriseCustomerOrganisation;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const updateMutation = useMutation(updateEnterpriseCustomerOrganisationMutationOptions());
  const {
    control,
    setFocus,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { organizationName: organisation.organizationName },
  });

  FormDialog.useAutoFocusField(setFocus, "organizationName");

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form
        onSubmit={handleSubmit(({ organizationName }) =>
          updateMutation.mutate(
            { organizationName, id: organisation.id },
            {
              onSuccess: onClose,
              onError: error => {
                hasErrorCode(error, ENTERPRISE_CUSTOMER_ORGANISATION_NAME_ALREADY_EXISTS)
                  ? setError(
                      "organizationName",
                      {
                        type: "organisationAlreadyExists",
                        message: t("EnterpriseCustomer.Organisations.NameAlreadyExists"),
                      },
                      { shouldFocus: true }
                    )
                  : setError("root.serverError", { type: "fallback" });
              },
            }
          )
        )}
      >
        <FormDialog.Title>{t("EnterpriseCustomer.Organisation.EditDialog.Header")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={Boolean(errors?.root?.serverError)} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="organizationName"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("EnterpriseCustomer.Field.Name")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateMutation.isPending}>
            {t("EnterpriseCustomer.Organisation.EditDialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
